import { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';

import { useMember } from 'modules/common/queries/member';
import getLastTwelveMonths from 'modules/HubV2/Bookkeeping/utils/getLastTwelveMonths';
import useHurdlrPlaidItems from 'services/hurdlr/queries/useHurdlrPlaidItems';
import {
    BankAccountStatus,
    PlaidItem,
} from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';
import usePlaidLinkToken from 'services/plaid/mutations/usePlaidLinkToken';
import usePlaidLinkSuccess from 'services/plaid/mutations/usePlaidLinkSuccess';

interface Props {
    plaidItemId?: string;
    onPlaidModalOpen?: () => void;
    onSuccess: () => void;
    onExit: () => void;
    onError?: (message: string) => void;
}

const useReconnectPlaidAccount = ({ plaidItemId, onSuccess, onExit, onPlaidModalOpen, onError }: Props) => {
    const [linkToken, setLinkToken] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [hasOpenedModal, setHasOpenedModal] = useState(false);
    const { data: member } = useMember();

    const { data: institutions, isSuccess: hasLoadedAccounts } = useHurdlrPlaidItems({
        memberId: member?.id,
        status: BankAccountStatus.Active,
        include_bank_accounts: true,
    });

    const { mutate: getPlaidLinkToken } = usePlaidLinkToken();
    const { mutate: notifyPlaidLinkSuccess } = usePlaidLinkSuccess();

    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: (_, metadata) => {
            // Notify backend about successful Plaid link repair
            if (plaidItemId) {
                notifyPlaidLinkSuccess({ item_id: plaidItemId, metadata });
            }
            onSuccess();
        },
        onExit,
    });

    useEffect(() => {
        if (linkToken) {
            return;
        }

        if (!hasLoadedAccounts || !institutions || !plaidItemId) {
            return;
        }

        const taskInstitution = institutions.find((item: PlaidItem) => item.apiItemId === plaidItemId);

        if (!taskInstitution) {
            setErrorMessage('Sorry, we could not find the account you are trying to reconnect. Please, try again.');
            return;
        }

        getPlaidLinkToken(
            {
                access_token: taskInstitution.apiAccessToken,
                statements: getLastTwelveMonths(),
            },
            {
                onSuccess: (plaidLinkTokenData) => setLinkToken(plaidLinkTokenData.link_token),
                onError: () => setErrorMessage('Sorry, we could not connect to Plaid. Please, try again.'),
            }
        );
    }, [hasLoadedAccounts, institutions, getPlaidLinkToken, plaidItemId]);

    useEffect(() => {
        // Prevent duplicate modals
        if (hasOpenedModal) {
            return;
        }

        if (ready) {
            open();
            onPlaidModalOpen?.();
            setHasOpenedModal(true);
        }
    }, [ready, open, onPlaidModalOpen]);

    useEffect(() => {
        if (errorMessage) {
            onError?.(errorMessage);
        }
    }, [errorMessage]);

    return {
        hasLoadedAccounts,
        errorMessage,
    };
};

export default useReconnectPlaidAccount;
